import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2, Clock, Zap, XCircle, User,
  Phone, MapPin, Calendar, Mail,
  Loader2, ArrowRight, StickyNote, X,
  Inbox, ClipboardList, RefreshCw, AlertTriangle, Star,
  Camera, ImagePlus, Trash2, Upload, Activity,
  Gauge, Wifi, ChevronDown, ChevronRight, Users, Network, CreditCard, Settings
} from 'lucide-react'
import { taskService } from '@/services/firebase/tasks'
import { storageService } from '@/services/firebase/storage'
import { useAuthStore } from '@/store/authStore'
import type { Task, TaskStatus, TaskType } from '@/types'
import toast from 'react-hot-toast'

// ── Helper: Determine enquiry workflow phase ──────────────────────────────────
const getEnquiryPhase = (task: Task): { label: string; icon: string; grad: string; dot: string } | null => {
  if (task.type !== 'enquiry') return null
  const isComplete = task.installationPhase === 'completed' || task.status === 'closed'
  const instPhase = task.installationPhase
  const isProvisioned = task.provisioningStage === 'provisioned'
  const isProvisioning = !!task.provisioningStage && task.provisioningStage !== 'pending'
  
  if (isComplete)    return { label: 'Completed',    icon: '✅', grad: 'linear-gradient(135deg,#059669,#10b981)', dot: '#059669' }
  
  // Show specific installation phase labels
  if (instPhase === 'pending_approval')
    return { label: 'Pending Approval', icon: '⏳', grad: 'linear-gradient(135deg,#f59e0b,#f97316)', dot: '#f59e0b' }
  if (instPhase === 'speed_test')
    return { label: 'Speed Test', icon: '⚡', grad: 'linear-gradient(135deg,#06b6d4,#3b82f6)', dot: '#06b6d4' }
  if (instPhase === 'installation_done')
    return { label: 'Install Done', icon: '✓', grad: 'linear-gradient(135deg,#14b8a6,#10b981)', dot: '#14b8a6' }
  if (instPhase && instPhase !== 'pending')
    return { label: 'Installation', icon: '🔧', grad: 'linear-gradient(135deg,#15803d,#22c55e)', dot: '#15803d' }
  
  if (isProvisioned || isProvisioning || task.feasibilityApproved)
    return { label: 'Provisioning', icon: '📦', grad: 'linear-gradient(135deg,#c2410c,#f97316)', dot: '#c2410c' }
  if (task.feasibilityStatus)
    return { label: 'Feasibility',  icon: '🔍', grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)', dot: '#6366f1' }
  return null
}

// Status badge override for enquiry tasks — reflects actual workflow phase instead of stale task.status
const getEnquiryStatusBadge = (task: Task): { label: string; bg: string; text: string; border: string; icon: React.ReactNode; dot: string } | null => {
  if (task.type !== 'enquiry') return null
  const isComplete = task.installationPhase === 'completed' || task.status === 'closed'
  const instPhase = task.installationPhase
  const isProvisioned = task.provisioningStage === 'provisioned'
  const isProvisioning = !!task.provisioningStage && task.provisioningStage !== 'pending'
  
  if (isComplete)
    return { label: 'Completed', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle2 size={10} />, dot: '#10b981' }
  
  // Installation phases - show specific status based on installation phase
  if (instPhase === 'pending_approval')
    return { label: 'Pending Approval', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: <Clock size={10} />, dot: '#f59e0b' }
  if (instPhase === 'speed_test')
    return { label: 'Speed Test', bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', icon: <Gauge size={10} />, dot: '#06b6d4' }
  if (instPhase === 'installation_done')
    return { label: 'Install Done', bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', icon: <CheckCircle2 size={10} />, dot: '#14b8a6' }
  if (instPhase && instPhase !== 'pending')
    return { label: 'Installing', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: <Zap size={10} />, dot: '#15803d' }
  
  if (isProvisioned || isProvisioning)
    return { label: 'In Progress', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', icon: <Zap size={10} />, dot: '#8b5cf6' }
  if (task.feasibilityApproved)
    return { label: 'Feasibility Approved', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle2 size={10} />, dot: '#10b981' }
  if (task.feasibilityStatus)
    return { label: 'Feasibility Review', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', icon: <Zap size={10} />, dot: '#6366f1' }
  return null
}

// ── Config ─────────────────────────────────────────────────────────────────────
const TYPE_MAP: Record<TaskType, { label: string; icon: string; grad: string; dot: string }> = {
  new_connection: { label: 'New Connection', icon: '🔌', grad: 'linear-gradient(135deg,#2563eb,#06b6d4)', dot: '#2563eb' },
  support:        { label: 'Support',        icon: '🛠️', grad: 'linear-gradient(135deg,#f97316,#ef4444)', dot: '#f97316' },
  enquiry:        { label: 'Enquiry',        icon: '💬', grad: 'linear-gradient(135deg,#8b5cf6,#6366f1)', dot: '#8b5cf6' },
  others:         { label: 'Others',         icon: '📋', grad: 'linear-gradient(135deg,#64748b,#475569)', dot: '#64748b' },
}

const STATUS_CFG: Record<TaskStatus, { label: string; bg: string; text: string; border: string; icon: React.ReactNode; dot: string }> = {
  open:             { label: 'Available',        bg: 'bg-sky-50',     text: 'text-sky-700',    border: 'border-sky-200',    icon: <Inbox size={10} />,        dot: '#0ea5e9' },
  assigned:         { label: 'New Assignment',   bg: 'bg-blue-50',    text: 'text-blue-700',   border: 'border-blue-200',   icon: <User size={10} />,         dot: '#3b82f6' },
  in_progress:      { label: 'In Progress',      bg: 'bg-violet-50',  text: 'text-violet-700', border: 'border-violet-200', icon: <Zap size={10} />,          dot: '#8b5cf6' },
  site_visit:       { label: 'Go to Site',       bg: 'bg-indigo-50',  text: 'text-indigo-700', border: 'border-indigo-200', icon: <MapPin size={10} />,       dot: '#6366f1' },
  work_in_progress: { label: 'Working',          bg: 'bg-orange-50',  text: 'text-orange-700', border: 'border-orange-200', icon: <Zap size={10} />,          dot: '#f97316' },
  pending_approval: { label: 'Waiting for Approval', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: <Clock size={10} />,        dot: '#f59e0b' },
  pending_feedback: { label: 'Feedback Required', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: <Star size={10} />,         dot: '#a855f7' },
  closed:           { label: 'Completed',        bg: 'bg-emerald-50', text: 'text-emerald-700',border: 'border-emerald-200',icon: <CheckCircle2 size={10} />, dot: '#10b981' },
  cancelled:        { label: 'Cancelled',        bg: 'bg-gray-50',    text: 'text-gray-500',   border: 'border-gray-200',   icon: <XCircle size={10} />,      dot: '#9ca3af' },
}

const PRIORITY_CFG: Record<string, { label: string; bar: string; badge: string; badgeText: string }> = {
  low:    { label: 'Low',    bar: '#94a3b8', badge: 'bg-gray-100',   badgeText: 'text-gray-500'   },
  medium: { label: 'Medium', bar: '#3b82f6', badge: 'bg-blue-100',   badgeText: 'text-blue-700'   },
  high:   { label: 'High',   bar: '#f97316', badge: 'bg-orange-100', badgeText: 'text-orange-700' },
  urgent: { label: 'Urgent', bar: '#ef4444', badge: 'bg-red-100',    badgeText: 'text-red-700'    },
}

// Engineer can advance through these stages
// Status transitions removed — engineers must open task to change status

const DONE_STATUSES: TaskStatus[] = ['closed', 'cancelled']
const ACTIVE_STATUSES: TaskStatus[] = ['assigned', 'in_progress', 'site_visit', 'work_in_progress', 'pending_approval', 'pending_feedback']

// ── Task Card ─────────────────────────────────────────────────────────────────
const TaskCard = ({ task, onAction, onClick }: {
  task: Task
  onAction: (t: Task) => void
  onClick: () => void
}) => {
  const phase = getEnquiryPhase(task)
  const t = phase ?? TYPE_MAP[task.type]
  const enquiryBadge = getEnquiryStatusBadge(task)
  const s = enquiryBadge ?? STATUS_CFG[task.status]
  const p = PRIORITY_CFG[task.priority]
  const displayType = task.type === 'others' && task.customType ? task.customType : t.label
  const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div onClick={onClick}
      className="group relative bg-white rounded-3xl overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-1 flex flex-col border-l-[4px]"
      style={{
        boxShadow: '0 2px 8px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)',
        borderLeftColor: t.dot,
      }}>

      {/* Priority bar */}
      <div className="h-1 w-full shrink-0" style={{ background: p.bar }} />

      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-10 h-10 rounded-2xl shrink-0 flex items-center justify-center text-white text-xs font-black"
            style={{ background: t.grad }}>
            {initials}
          </div>
          <div className="min-w-0">
            <p className="font-black text-gray-900 text-sm truncate leading-tight">{task.customerName}</p>
            <p className="text-[10px] font-mono text-blue-500 mt-0.5 tracking-wide">{task.taskNumber}</p>
          </div>
        </div>
        <span className={`shrink-0 flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
          {s.icon}{s.label}
        </span>
      </div>

      {/* Body */}
      <div className="px-4 space-y-1.5 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs">{t.icon}</span>
          <span className="text-xs font-bold text-gray-600">{displayType}</span>
          <span className={`ml-auto text-[9px] font-black px-2 py-0.5 rounded-full ${p.badge} ${p.badgeText}`}>
            {p.label.toUpperCase()}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Phone size={10} className="text-gray-400 shrink-0" />
          <span className="font-medium">+91 {task.customerPhone}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500 truncate">
          <MapPin size={10} className="text-gray-400 shrink-0" />
          <span className="truncate">{task.city}{task.location ? ` · ${task.location}` : ''}</span>
        </div>
        {task.description && (
          <p className="text-[11px] text-gray-400 line-clamp-2 pt-1 leading-relaxed">{task.description}</p>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pt-3 pb-4 mt-3 border-t border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-1 text-[10px] text-gray-400">
          <Calendar size={9} />
          {task.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
        </div>
        <button
          onClick={e => { e.stopPropagation(); onAction(task) }}
          className="flex items-center gap-1.5 text-[10px] font-black px-3 py-1.5 rounded-xl text-white transition-all hover:scale-[1.04] active:scale-[0.97]"
          style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          <ArrowRight size={10} /> View
        </button>
      </div>
    </div>
  )
}

// ── Detail Modal ───────────────────────────────────────────────────────────────
export const _TaskDetailModal = ({ task, onClose, onStatusChange, onReload }: {
  task: Task; onClose: () => void
  onStatusChange: (id: string, s: TaskStatus) => void
  onReload?: () => void
}) => {
  const phase = getEnquiryPhase(task)
  const t = phase ?? TYPE_MAP[task.type]
  const enquiryBadge = getEnquiryStatusBadge(task)
  const s = enquiryBadge ?? STATUS_CFG[task.status]
  const p = PRIORITY_CFG[task.priority]
  const displayType = task.type === 'others' && task.customType ? task.customType : t.label
  const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [rating, setRating] = useState(5)
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null)
  const [feedbackText, setFeedbackText] = useState('')
  const [catRatings, setCatRatings] = useState({ serviceQuality: 5, responseTime: 5, communication: 5, workmanship: 5 })
  const [submitting, setSubmitting] = useState(false)

  // Verification ticks and mismatch states
  const [tickedFields, setTickedFields] = useState<Record<string, boolean>>(() => task.verificationTicked || {})
  const [mismatchReportOpen, setMismatchReportOpen] = useState(false)
  const [mismatchComments, setMismatchComments] = useState(task.mismatchComments || '')
  const [reportingMismatch, setReportingMismatch] = useState(false)

  const handleToggleTick = async (key: string) => {
    const nextTicks = { ...tickedFields, [key]: !tickedFields[key] }
    setTickedFields(nextTicks)
    await taskService.updateVerificationTicks(task.id, nextTicks)
  }

  const handleReportMismatch = async () => {
    if (!mismatchComments.trim()) {
      toast.error('Please enter mismatch details')
      return
    }
    setReportingMismatch(true)
    const res = await taskService.reportMismatch(task.id, mismatchComments.trim())
    setReportingMismatch(false)
    if (res.success) {
      toast.success('Mismatch reported successfully')
      task.mismatchFlag = true
      task.mismatchComments = mismatchComments.trim()
      setMismatchReportOpen(false)
    } else {
      toast.error(res.error ?? 'Failed to report mismatch')
    }
  }

  // Accordion section state
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    customer: true,
    connection: true,
    plan: false,
    technical: false
  })

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  // Field verification groups - ALL fields shown (filled or unfilled)
  const verificationGroups = [
    {
      key: 'customer',
      title: 'Customer Information',
      icon: Users,
      fields: [
        { key: 'customerName', label: 'Company Name (Customer Name)', value: task.customerName || '—' },
        { key: 'customerPhone', label: 'Mobile Number', value: task.customerPhone ? `+91 ${task.customerPhone}` : '—' },
        { key: 'customerEmail', label: 'Email Address', value: task.customerEmail || '—' },
        { key: 'address', label: 'Installation Address', value: task.address || '—' },
        { key: 'location', label: 'Building / Landmark', value: task.location || '—' },
        { key: 'city', label: 'City', value: task.city || '—' },
      ]
    },
    {
      key: 'connection',
      title: 'Connection Parameters',
      icon: Network,
      fields: [
        { key: 'serviceNumber', label: 'Service Number', value: task.serviceNumber || '—' },
        { key: 'bbUserId', label: 'Broadband User ID', value: task.bbUserId || '—' },
        { key: 'voipPassword', label: 'VoIP Password', value: task.voipPassword || '—' },
        { key: 'activationDate', label: 'Activation Date', value: task.activationDate || '—' },
      ]
    },
    {
      key: 'technical',
      title: 'Technical Settings',
      icon: Settings,
      fields: [
        { key: 'oltIp', label: 'OLT IP Address', value: task.oltIp || '—' },
        { key: 'vlan', label: 'VLAN ID', value: task.vlan !== undefined && task.vlan !== null ? String(task.vlan) : '—' },
        { key: 'port', label: 'Port Number', value: task.port !== undefined && task.port !== null ? String(task.port) : '—' },
        { key: 'exchangeCode', label: 'Exchange Code', value: task.exchangeCode || '—' },
        { key: 'category', label: 'Category Code', value: task.category || '—' },
        { key: 'subServiceType', label: 'Service Type', value: task.subServiceType || '—' },
      ]
    },
    {
      key: 'plan',
      title: 'Plan & Billing',
      icon: CreditCard,
      fields: [
        { key: 'subscriptionPlan', label: 'Subscription Plan', value: task.subscriptionPlan || '—' },
        { key: 'planPeriod', label: 'Plan Period', value: task.planPeriod || '—' },
        { key: 'billingAccountNo', label: 'Billing Account No', value: task.billingAccountNo ? String(task.billingAccountNo) : '—' },
        { key: 'fmc', label: 'Monthly FMC', value: task.fmc !== undefined && task.fmc !== null ? `Rs. ${task.fmc}` : '—' },
      ]
    }
  ]

  // Flat list for verification count
  const allFields = verificationGroups.flatMap(g => g.fields)

  // Feasibility study state (only for enquiries in progress)
  const [feasibilityOpen, setFeasibilityOpen] = useState(false)
  const [feasStatus, setFeasStatus] = useState<'feasible' | 'not_feasible'>('feasible')
  const [feasComments, setFeasComments] = useState('')
  const [feasWorkflows, setFeasWorkflows] = useState<string[]>([])
  const [feasSubmitting, setFeasSubmitting] = useState(false)

  // Geolocation state for feasibility study
  const [feasLat, setFeasLat] = useState<number | undefined>(undefined)
  const [feasLng, setFeasLng] = useState<number | undefined>(undefined)
  const [fetchingLoc, setFetchingLoc] = useState(false)
  const [locError, setLocError] = useState<string | null>(null)

  const fetchLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocError('Geolocation is not supported by your browser')
      return
    }
    setFetchingLoc(true)
    setLocError(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFeasLat(pos.coords.latitude)
        setFeasLng(pos.coords.longitude)
        setFetchingLoc(false)
      },
      (err) => {
        let msg = 'Unknown location error'
        if (err.code === 1) msg = 'Location permission denied'
        else if (err.code === 2) msg = 'Location unavailable'
        else if (err.code === 3) msg = 'Location request timeout'
        setLocError(msg)
        setFetchingLoc(false)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }, [])

  useEffect(() => {
    if (feasibilityOpen) {
      fetchLocation()
    }
  }, [feasibilityOpen, fetchLocation])

  // Speed test states
  const [testDlSpeed, setTestDlSpeed] = useState<number | null>(task.speedTestDownload || null)
  const [testUlSpeed, setTestUlSpeed] = useState<number | null>(task.speedTestUpload || null)
  const [testPing, setTestPing] = useState<number | null>(task.speedTestPing || null)
  const [testIsp, setTestIsp] = useState<string | null>(task.speedTestIsp || null)
  const [testIp, setTestIp] = useState<string | null>(task.speedTestIp || null)
  const [testingState, setTestingState] = useState<'idle' | 'checking_isp' | 'ping' | 'download' | 'upload' | 'saving' | 'done'>('idle')
  const [speedTestProgress, setSpeedTestProgress] = useState(0)
  const [speedTestError, setSpeedTestError] = useState<string | null>(null)
  const [ispOverride, setIspOverride] = useState(false)

  const runSpeedTest = async () => {
    setTestingState('checking_isp')
    setSpeedTestError(null)
    setSpeedTestProgress(0)

    try {
      let ip = '0.0.0.0'
      let isp = 'Unknown'
      
      try {
        const res = await fetch('https://ipapi.co/json')
        if (res.ok) {
          const data = await res.json()
          ip = data.ip || '0.0.0.0'
          isp = data.org || data.isp || 'Unknown'
        }
      } catch (err) {
        try {
          const res = await fetch('https://ip-api.com/json')
          if (res.ok) {
            const data = await res.json()
            ip = data.query || '0.0.0.0'
            isp = data.isp || 'Unknown'
          }
        } catch (err2) {
          // Fallback
        }
      }

      setTestIp(ip)
      setTestIsp(isp)

      const isBsnl = isp.toLowerCase().includes('bsnl') || 
                     isp.toLowerCase().includes('sanchar') || 
                     isp.toLowerCase().includes('bharat') ||
                     isp.toLowerCase().includes('telecom')

      if (!isBsnl && !ispOverride) {
        setSpeedTestError(`⚠️ Network Check Failed: Connected to ${isp}. Please connect to the customer's BSNL modem's Wi-Fi network.`)
        setTestingState('idle')
        return
      }

      setSpeedTestProgress(10)

      // Latency/Ping
      setTestingState('ping')
      let totalRtt = 0
      const pingIterations = 3
      for (let i = 0; i < pingIterations; i++) {
        const start = performance.now()
        await fetch('https://speed.cloudflare.com/__down?bytes=0', { cache: 'no-store' })
        const end = performance.now()
        totalRtt += (end - start)
        setSpeedTestProgress(10 + Math.round(((i + 1) / pingIterations) * 20))
      }
      const ping = Math.round(totalRtt / pingIterations)
      setTestPing(ping)

      // Download Speed Test
      setTestingState('download')
      const dlStart = performance.now()
      const dlRes = await fetch('https://speed.cloudflare.com/__down?bytes=2000000', { cache: 'no-store' })
      const dlBlob = await dlRes.blob()
      const dlEnd = performance.now()
      const dlDuration = (dlEnd - dlStart) / 1000
      const dlBits = dlBlob.size * 8
      const dlSpeed = Number(((dlBits / dlDuration) / (1024 * 1024)).toFixed(2))
      setTestDlSpeed(dlSpeed)
      setSpeedTestProgress(60)

      // Upload Speed Test
      setTestingState('upload')
      const ulStart = performance.now()
      const ulData = new Uint8Array(1000000)
      await fetch('https://speed.cloudflare.com/__up', {
        method: 'POST',
        body: ulData,
        cache: 'no-store'
      })
      const ulEnd = performance.now()
      const ulDuration = (ulEnd - ulStart) / 1000
      const ulBits = ulData.length * 8
      const ulSpeed = Number(((ulBits / ulDuration) / (1024 * 1024)).toFixed(2))
      setTestUlSpeed(ulSpeed)
      setSpeedTestProgress(85)

      // Save Results
      setTestingState('saving')
      const recordRes = await taskService.recordSpeedTest(task.id, {
        download: dlSpeed,
        upload: ulSpeed,
        ping: ping,
        isp: isp,
        ip: ip
      })

      if (recordRes.success) {
        setSpeedTestProgress(100)
        setTestingState('done')
        toast.success('Speed test completed and recorded! 🚀')
        if (onReload) onReload()
      } else {
        throw new Error(recordRes.error || 'Failed to save results')
      }
    } catch (err) {
      setSpeedTestError(err instanceof Error ? err.message : 'Network speed test failed. Please try again.')
      setTestingState('idle')
    }
  }

  const isEnquiryInProgress = task.type === 'enquiry' && task.status === 'in_progress'

  // Site photo upload state (only active on site_visit status)
  const [pendingPhotos, setPendingPhotos] = useState<{ file: File; preview: string }[]>([])
  const [uploadProgress, setUploadProgress] = useState<number[]>([])
  const [uploading, setUploading] = useState(false)
  const [savedUrls, setSavedUrls] = useState<string[]>(task.sitePhotos ?? [])

  const isSiteVisit = task.status === 'site_visit'
  const isSpeedTestRequired = (task.type === 'new_connection' || task.type === 'support') && task.status === 'work_in_progress'

  // Installation phase management for enquiry tasks (field engineer)
  const [instPhaseSaving, setInstPhaseSaving] = useState(false)
  const canManageInstPhase = task.type === 'enquiry' && task.provisioningStage === 'provisioned' && task.installationEngineerName
  const currentInstPhase = task.installationPhase ?? 'pending'

  const handleUpdateInstPhase = async (phase: string) => {
    setInstPhaseSaving(true)
    const res = await taskService.updateInstallationPhase(task.id, phase)
    if (res.success) {
      toast.success(`Installation phase updated: ${phase}`)
      onReload?.()
    } else {
      toast.error(res.error ?? 'Failed to update phase')
    }
    setInstPhaseSaving(false)
  }

  const handlePickPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    const newEntries = files.map(f => ({ file: f, preview: URL.createObjectURL(f) }))
    setPendingPhotos(prev => [...prev, ...newEntries])
    e.target.value = ''
  }

  const removePending = (idx: number) => {
    setPendingPhotos(prev => {
      URL.revokeObjectURL(prev[idx].preview)
      return prev.filter((_, i) => i !== idx)
    })
  }

  const handleUploadPhotos = async () => {
    if (pendingPhotos.length === 0) return
    setUploading(true)
    setUploadProgress(pendingPhotos.map(() => 0))
    const urls: string[] = [...savedUrls]
    for (let i = 0; i < pendingPhotos.length; i++) {
      const { file } = pendingPhotos[i]
      const compressed = await storageService.compressImage(file, 1920, 1080, 0.8)
      const path = storageService.generateFilePath(`tasks/${task.id}/site_photos`, file.name)
      const res = await storageService.uploadFile(path, compressed, (p) => {
        setUploadProgress(prev => { const n = [...prev]; n[i] = p; return n })
      })
      if (res.success && res.data) urls.push(res.data)
      else { toast.error(`Failed to upload ${file.name}`); setUploading(false); return }
    }
    const saveRes = await taskService.attachSitePhotos(task.id, urls)
    if (saveRes.success) {
      setSavedUrls(urls)
      setPendingPhotos([])
      setUploadProgress([])
      toast.success(`${pendingPhotos.length} photo${pendingPhotos.length > 1 ? 's' : ''} uploaded ✔`)
    } else {
      toast.error(saveRes.error ?? 'Could not save photos')
    }
    setUploading(false)
  }

  const handleFeasibilitySubmit = async () => {
    if (feasLat === undefined || feasLng === undefined) {
      toast.error('GPS coordinates must be acquired to submit feasibility study')
      return
    }
    setFeasSubmitting(true)
    const res = await taskService.submitFeasibilityStudy(task.id, {
      feasibilityStatus: feasStatus,
      feasibilityComments: feasComments,
      feasibilityWorkflowRequired: feasWorkflows,
      latitude: feasLat,
      longitude: feasLng
    })
    if (res.success) {
      toast.success('Feasibility study submitted! Awaiting admin review.')
      onStatusChange(task.id, 'pending_approval')
      onClose()
    } else {
      toast.error(res.error ?? 'Submission failed')
    }
    setFeasSubmitting(false)
  }

  const handleFeedbackSubmit = async () => {
    if (wouldRecommend === null) { toast.error('Please select a recommendation option'); return }
    setSubmitting(true)
    const res = await taskService.submitFeedback(task.id, {
      rating,
      feedback: feedbackText,
      wouldRecommend,
      categories: catRatings,
    })
    if (res.success) { toast.success('Feedback saved — ticket closed! 🎉'); onStatusChange(task.id, 'closed'); onClose() }
    else toast.error(res.error ?? 'Submit failed')
    setSubmitting(false)
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col"
        style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="relative shrink-0 px-5 pt-5 pb-5"
          style={{ background: t.grad }}>
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${t.dot}44 0%, transparent 70%)`, filter: 'blur(24px)' }} />
          <div className="sm:hidden flex justify-center mb-4">
            <div className="w-10 h-1 bg-white/20 rounded-full" />
          </div>
          <div className="relative z-10 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-black text-base shrink-0"
                style={{ background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)', color: 'white', boxShadow: `0 4px 16px ${t.dot}55` }}>
                {initials}
              </div>
              <div>
                <p className="font-black text-white text-base leading-tight">{task.customerName}</p>
                <p className="text-[11px] font-mono text-blue-300/80 mt-0.5">{task.taskNumber}</p>
              </div>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors shrink-0">
              <X size={16} />
            </button>
          </div>
          <div className="relative z-10 flex items-center gap-2 mt-4 flex-wrap">
            <span className="flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full text-white"
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)' }}>
              {t.icon} {displayType}
            </span>
            <span className={`flex items-center gap-1 text-[11px] font-black px-3 py-1.5 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
              {s.icon}{s.label}
            </span>
            <span className={`text-[11px] font-black px-3 py-1.5 rounded-full ${p.badge} ${p.badgeText}`}>
              {p.label} Priority
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Phone,    label: 'Phone',   value: `+91 ${task.customerPhone}` },
              { icon: MapPin,   label: 'City',    value: task.city },
              { icon: Calendar, label: 'Created', value: task.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) },
              ...(task.customerEmail ? [{ icon: Mail, label: 'Email', value: task.customerEmail }] : []),
            ].map(row => (
              <div key={row.label} className="bg-gray-50 rounded-2xl px-3.5 py-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <row.icon size={11} className="text-gray-400" />
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{row.label}</p>
                </div>
                <p className="text-sm font-bold text-gray-800 truncate">{row.value}</p>
              </div>
            ))}
          </div>

          <div className="bg-gray-50 rounded-2xl px-3.5 py-3">
            <div className="flex items-center gap-1.5 mb-1">
              <MapPin size={11} className="text-gray-400" />
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Address</p>
            </div>
            <p className="text-sm font-semibold text-gray-800 leading-relaxed">{task.address}</p>
            {task.location && <p className="text-xs text-gray-500 mt-0.5">{task.location}</p>}
          </div>

          {/* BSNL Connection Details Card (hidden during enquiry/feasibility) */}
          {!(task.type === 'enquiry' && (!task.provisioningStage || task.provisioningStage === 'pending')) && (task.bbUserId || task.serviceNumber || task.oltIp || task.subscriptionPlan) && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-3.5 text-left">
              <div className="flex items-center gap-2 border-b border-slate-150 pb-2">
                <div className="w-5 h-5 rounded-lg flex items-center justify-center bg-blue-50">
                  <Activity size={10} className="text-blue-500" />
                </div>
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">BSNL Connection Parameters</h3>
              </div>
              <div className="grid grid-cols-2 gap-3.5">
                {task.bbUserId && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Broadband User ID</span>
                    <span className="text-xs font-bold text-gray-800 font-mono bg-slate-50 px-1.5 py-0.5 rounded border border-gray-100 inline-block mt-0.5 select-all">{task.bbUserId}</span>
                  </div>
                )}
                {task.serviceNumber && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Service Number</span>
                    <span className="text-xs font-bold text-gray-800 mt-0.5 block">{task.serviceNumber}</span>
                  </div>
                )}
                {task.oltIp && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">OLT IP Address</span>
                    <span className="text-xs font-bold text-gray-800 font-mono bg-slate-50 px-1.5 py-0.5 rounded border border-gray-100 inline-block mt-0.5 select-all">{task.oltIp}</span>
                  </div>
                )}
                {(task.vlan !== null || task.port !== null) && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">VLAN / Port</span>
                    <span className="text-xs font-bold text-indigo-700 font-mono mt-0.5 block">
                      {task.vlan !== null ? task.vlan : '—'} / {task.port !== null ? task.port : '—'}
                    </span>
                  </div>
                )}
                {task.exchangeCode && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Exchange Code</span>
                    <span className="text-xs font-semibold text-gray-700 mt-0.5 block">{task.exchangeCode}</span>
                  </div>
                )}
                {task.category && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Category Code</span>
                    <span className="text-xs font-semibold text-gray-700 mt-0.5 block">{task.category}</span>
                  </div>
                )}
                {task.voipPassword && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">VoIP Password</span>
                    <span className="text-xs font-semibold text-gray-700 font-mono mt-0.5 block">{task.voipPassword}</span>
                  </div>
                )}
                {task.activationDate && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Activation Date</span>
                    <span className="text-xs font-semibold text-gray-750 font-mono mt-0.5 block">{task.activationDate}</span>
                  </div>
                )}
                {task.subscriptionPlan && (
                  <div className="col-span-2">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Subscription Plan</span>
                    <span className="text-xs font-bold text-gray-900 mt-0.5 block leading-normal">{task.subscriptionPlan}</span>
                  </div>
                )}
                {task.billingAccountNo && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Billing Account No.</span>
                    <span className="text-xs font-semibold text-gray-700 font-mono mt-0.5 block">{task.billingAccountNo}</span>
                  </div>
                )}
                {task.fmc !== undefined && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Monthly FMC</span>
                    <span className="text-xs font-extrabold text-emerald-600 mt-0.5 block">Rs. {task.fmc}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Details Mismatch Banner (if mismatch reported) */}
          {task.mismatchFlag && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-left">
              <div className="flex items-center gap-2 text-red-700 font-extrabold text-sm mb-1.5">
                <AlertTriangle size={16} /> Mismatch Reported
              </div>
              <p className="text-xs text-red-600 leading-relaxed font-semibold">"{task.mismatchComments}"</p>
              <p className="text-[10px] text-red-400 mt-1.5">Awaiting correction from the admin side.</p>
            </div>
          )}

          {/* Speed Test Card */}
          {(isSpeedTestRequired || task.speedTestDownload) && (
            <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm space-y-3.5 text-left">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div className="flex items-center gap-2">
                  <Gauge size={15} className="text-indigo-500" />
                  <p className="text-xs font-black text-gray-700 uppercase tracking-wider">Modem Speed Test Verification</p>
                </div>
                {testDlSpeed && (
                  <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center gap-1">
                    <CheckCircle2 size={10} /> Recorded
                  </span>
                )}
              </div>

              {testingState === 'idle' && !testDlSpeed && (
                <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 flex flex-col items-center text-center gap-3">
                  <Wifi size={24} className="text-indigo-400" />
                  <div>
                    <p className="text-xs font-black text-gray-750">Connect to Installed Modem</p>
                    <p className="text-[10px] text-gray-500 mt-1 max-w-[280px]">
                      Ensure you are connected to the customer's new BSNL Wi-Fi network before initiating the speed test.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={runSpeedTest}
                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-xs font-black rounded-xl transition-all shadow-md active:scale-95 flex items-center gap-1.5"
                  >
                    <Gauge size={13} /> Start Speed Test
                  </button>
                </div>
              )}

              {testingState !== 'idle' && testingState !== 'done' && (
                <div className="bg-indigo-50/20 border border-indigo-100/50 rounded-xl p-4 text-center space-y-3">
                  <Loader2 size={24} className="animate-spin text-indigo-500 mx-auto" />
                  <div>
                    <p className="text-xs font-black text-indigo-900 capitalize animate-pulse">
                      {testingState === 'checking_isp' && '🔍 Checking ISP Provider...'}
                      {testingState === 'ping' && '⚡ Testing Latency (Ping)...'}
                      {testingState === 'download' && '📥 Downloading Test File...'}
                      {testingState === 'upload' && '📤 Uploading Test Payload...'}
                      {testingState === 'saving' && '💾 Saving Speed Test Results...'}
                    </p>
                    <div className="w-full bg-gray-150 h-1.5 rounded-full mt-2 overflow-hidden max-w-[200px] mx-auto">
                      <div className="bg-indigo-500 h-full rounded-full transition-all duration-300" style={{ width: `${speedTestProgress}%` }} />
                    </div>
                  </div>
                </div>
              )}

              {speedTestError && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-3.5 text-xs text-red-755 flex flex-col gap-2.5">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <p className="font-semibold leading-relaxed">{speedTestError}</p>
                  </div>
                  {!testDlSpeed && (
                    <div className="flex flex-col gap-2 pt-2 border-t border-red-100/60">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={ispOverride}
                          onChange={e => {
                            setIspOverride(e.target.checked)
                            if (e.target.checked) {
                              setSpeedTestError(null)
                            }
                          }}
                          className="mt-0.5 rounded border-red-300 text-red-600 focus:ring-red-500"
                        />
                        <span className="text-[10px] font-bold text-red-800">
                          Force override network check (use only if ISP lookup is misidentified)
                        </span>
                      </label>
                      {ispOverride && (
                        <button
                          type="button"
                          onClick={runSpeedTest}
                          className="px-3.5 py-2 bg-red-650 hover:bg-red-700 text-white font-black rounded-lg text-[10px] self-end shadow-sm transition-all"
                        >
                          Retry with Override
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {testDlSpeed && (
                <div className="space-y-3 bg-slate-50/50 border border-slate-100 rounded-xl p-3">
                  <div className="grid grid-cols-3 gap-2.5">
                    <div className="bg-white rounded-lg p-2.5 border border-gray-100 text-center">
                      <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider block">Download</span>
                      <span className="text-sm font-extrabold text-blue-600 mt-0.5 block">{testDlSpeed} Mbps</span>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 border border-gray-100 text-center">
                      <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider block">Upload</span>
                      <span className="text-sm font-extrabold text-emerald-600 mt-0.5 block">{testUlSpeed} Mbps</span>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 border border-gray-100 text-center">
                      <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider block">Ping</span>
                      <span className="text-sm font-extrabold text-indigo-600 mt-0.5 block">{testPing} ms</span>
                    </div>
                  </div>

                  <div className="text-[10px] space-y-1 px-1 text-gray-500 border-t border-slate-100/60 pt-2.5">
                    <div className="flex justify-between">
                      <span>Provider ISP:</span>
                      <span className="font-bold text-gray-700 truncate max-w-[180px]">{testIsp}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Client IP:</span>
                      <span className="font-mono text-gray-650">{testIp}</span>
                    </div>
                    {task.speedTestTimestamp && (
                      <div className="flex justify-between">
                        <span>Tested At:</span>
                        <span className="text-gray-600">
                          {new Date(task.speedTestTimestamp).toLocaleString('en-IN', { hour12: true, hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  </div>

                  {task.status === 'work_in_progress' && (
                    <button
                      type="button"
                      onClick={() => {
                        setTestDlSpeed(null)
                        setTestUlSpeed(null)
                        setTestPing(null)
                        setTestingState('idle')
                      }}
                      className="w-full text-center text-[10px] font-black text-indigo-600 hover:text-indigo-850 bg-indigo-50/50 py-2 rounded-lg transition-colors border border-indigo-100/40 mt-1"
                    >
                      🔄 Retest Connection
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Installation Phase Management for Field Engineers */}
          {canManageInstPhase && (
            <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm space-y-3 text-left">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div className="flex items-center gap-2">
                  <Wifi size={15} className="text-violet-500" />
                  <p className="text-xs font-black text-gray-700 uppercase tracking-wider">Installation Progress</p>
                </div>
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-100">
                  {currentInstPhase === 'pending' && 'Pending'}
                  {currentInstPhase === 'assigned' && 'Assigned'}
                  {currentInstPhase === 'in_progress' && 'In Progress'}
                  {currentInstPhase === 'site_visit' && 'Site Visit'}
                  {currentInstPhase === 'work_in_progress' && 'Working'}
                  {currentInstPhase === 'installation_done' && 'Install Done'}
                  {currentInstPhase === 'speed_test' && 'Speed Test'}
                  {currentInstPhase === 'pending_approval' && 'Pending Approval'}
                  {currentInstPhase === 'completed' && 'Completed'}
                </span>
              </div>

              {/* Mark Installation Completed - from work_in_progress */}
              {currentInstPhase === 'work_in_progress' && (
                <div className="space-y-2">
                  <p className="text-[10px] text-gray-500">Physical installation work is complete?</p>
                  <button
                    onClick={() => handleUpdateInstPhase('installation_done')}
                    disabled={instPhaseSaving}
                    className="w-full py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-xs font-black rounded-xl shadow-md active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {instPhaseSaving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Mark Installation Completed
                  </button>
                </div>
              )}

              {/* Run Speed Test - from installation_done */}
              {currentInstPhase === 'installation_done' && (
                <div className="space-y-2">
                  <p className="text-[10px] text-gray-500">Connect to customer modem WiFi and run speed test</p>
                  <button
                    onClick={() => handleUpdateInstPhase('speed_test')}
                    disabled={instPhaseSaving}
                    className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-xs font-black rounded-xl shadow-md active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {instPhaseSaving ? <Loader2 size={14} className="animate-spin" /> : <Gauge size={14} />}
                    Proceed to Speed Test
                  </button>
                </div>
              )}

              {/* Submit for Approval - from speed_test (requires speed test done) */}
              {currentInstPhase === 'speed_test' && (
                <div className="space-y-2">
                  {!task.speedTestDownload ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                      <p className="text-[10px] text-amber-700">⚠️ Complete speed test before submitting for approval</p>
                    </div>
                  ) : (
                    <p className="text-[10px] text-emerald-600">✓ Speed test completed. Ready to submit.</p>
                  )}
                  <button
                    onClick={() => handleUpdateInstPhase('pending_approval')}
                    disabled={instPhaseSaving || !task.speedTestDownload}
                    className="w-full py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-black rounded-xl shadow-md active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {instPhaseSaving ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                    Submit for Admin Approval
                  </button>
                  <p className="text-[9px] text-gray-400 text-center">Admin will review and finalize completion</p>
                </div>
              )}

              {/* Waiting for admin approval */}
              {currentInstPhase === 'pending_approval' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <Clock size={16} className="text-amber-500 mx-auto mb-1" />
                  <p className="text-xs font-bold text-amber-700">Pending Admin Approval</p>
                  <p className="text-[10px] text-amber-600 mt-1">Installation and speed test submitted. Awaiting admin review.</p>
                </div>
              )}

              {/* Completed by admin */}
              {currentInstPhase === 'completed' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <CheckCircle2 size={16} className="text-emerald-500 mx-auto mb-1" />
                  <p className="text-xs font-bold text-emerald-700">Installation Completed</p>
                  <p className="text-[10px] text-emerald-600 mt-1">Approved by admin. Ticket closed.</p>
                </div>
              )}
            </div>
          )}

          {/* Verification Checklist Card - Accordion Style */}
          {!DONE_STATUSES.includes(task.status) && (
            <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm space-y-3.5 text-left">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div className="flex items-center gap-2">
                  <ClipboardList size={15} className="text-blue-500" />
                  <p className="text-xs font-black text-gray-700 uppercase tracking-wider">Verify All Customer Data</p>
                </div>
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                  {allFields.filter(f => tickedFields[f.key]).length}/{allFields.length} verified
                </span>
              </div>

              {/* Accordion Sections */}
              <div className="space-y-2">
                {verificationGroups.map(group => {
                  const isOpen = openSections[group.key]
                  const verifiedCount = group.fields.filter(f => tickedFields[f.key]).length
                  const totalCount = group.fields.length
                  const isComplete = verifiedCount === totalCount

                  return (
                    <div key={group.key} className={`rounded-xl overflow-hidden border ${isComplete ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'}`}>
                      {/* Section Header */}
                      <button
                        type="button"
                        onClick={() => toggleSection(group.key)}
                        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50/50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <group.icon size={14} className={isComplete ? 'text-emerald-500' : 'text-slate-500'} />
                          <span className={`text-xs font-bold ${isComplete ? 'text-emerald-700' : 'text-slate-700'}`}>{group.title}</span>
                          <span className="text-[10px] text-slate-400">
                            ({verifiedCount}/{totalCount})
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {isComplete && <CheckCircle2 size={12} className="text-emerald-500" />}
                          {isOpen ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                        </div>
                      </button>

                      {/* Section Content */}
                      {isOpen && (
                        <div className="px-3 pb-3 space-y-1.5 border-t border-slate-100/50">
                          {group.fields.map(field => {
                            const isChecked = !!tickedFields[field.key]
                            const isEmpty = field.value === '—'
                            return (
                              <label
                                key={field.key}
                                className={`flex items-start gap-2.5 p-2 rounded-lg cursor-pointer transition-all ${
                                  isChecked ? 'bg-emerald-50/50' : isEmpty ? 'bg-amber-50/50' : 'bg-slate-50/50 hover:bg-slate-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => { e.stopPropagation(); handleToggleTick(field.key); }}
                                  className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-4 w-4 shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-wide block">{field.label}</span>
                                  <span className={`text-xs font-bold ${
                                    isChecked ? 'text-slate-600 line-through opacity-60' :
                                    isEmpty ? 'text-amber-600 italic' : 'text-slate-800'
                                  }`}>
                                    {field.value}
                                    {isEmpty && <span className="text-[9px] font-normal text-amber-500 ml-1">(not provided)</span>}
                                  </span>
                                </div>
                                {isChecked && <CheckCircle2 size={12} className="text-emerald-500 shrink-0 self-center" />}
                                {isEmpty && !isChecked && <span className="text-[9px] text-amber-500 shrink-0">⚠️</span>}
                              </label>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {!mismatchReportOpen ? (
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setMismatchReportOpen(true)}
                    className="w-full py-2.5 text-xs font-black text-red-500 bg-red-50 hover:bg-red-100/60 rounded-xl transition-all flex items-center justify-center gap-1.5 active:scale-95 animate-spring"
                  >
                    <AlertTriangle size={13} /> Report Mismatch
                  </button>
                </div>
              ) : (
                <div className="pt-2 border-t border-slate-100 space-y-2.5">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Details Mismatch Comments</label>
                    <textarea
                      value={mismatchComments}
                      onChange={e => setMismatchComments(e.target.value)}
                      placeholder="Specify which fields are incorrect and the correct details..."
                      rows={2.5}
                      className="w-full text-xs font-semibold text-gray-750 placeholder-gray-400 border border-gray-200 rounded-xl p-2.5 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all resize-none"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleReportMismatch}
                      disabled={reportingMismatch}
                      className="flex-1 py-2 text-white font-black rounded-xl text-xs bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                      {reportingMismatch && <Loader2 size={12} className="animate-spin" />}
                      Submit Report
                    </button>
                    <button
                      type="button"
                      onClick={() => setMismatchReportOpen(false)}
                      className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl text-xs transition-colors"
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {task.description && (
            <div className="rounded-2xl px-3.5 py-3" style={{ background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.1)' }}>
              <p className="text-[9px] font-black text-blue-500 uppercase tracking-widest mb-1.5">Description</p>
              <p className="text-sm text-gray-700 leading-relaxed">{task.description}</p>
            </div>
          )}

          {task.notes && (
            <div className="rounded-2xl px-3.5 py-3" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <StickyNote size={11} className="text-amber-500" />
                <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest">Internal Notes</p>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{task.notes}</p>
            </div>
          )}

          {/* Feasibility Study Summary (for inquiries) */}
          {task.type === 'enquiry' && task.feasibilityStatus && (
            <div className="rounded-2xl overflow-hidden"
              style={{ background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.12)' }}>
              <div className="flex items-center justify-between px-3.5 py-2.5 border-b"
                style={{ borderColor: 'rgba(99,102,241,0.1)' }}>
                <div className="flex items-center gap-2">
                  <Zap size={12} className="text-indigo-500" />
                  <p className="text-[9px] font-black text-indigo-600 uppercase tracking-widest">Feasibility Study</p>
                </div>
                <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${
                  task.feasibilityStatus === 'feasible' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                }`}>
                  {task.feasibilityStatus === 'feasible' ? '⚡ FEASIBLE' : '❌ NOT FEASIBLE'}
                </span>
              </div>
              <div className="p-3.5 space-y-2">
                {task.feasibilityComments && (
                  <div>
                    <p className="text-[9px] text-gray-400 font-bold uppercase">Additional Comments</p>
                    <p className="text-xs text-gray-700 leading-relaxed mt-0.5">{task.feasibilityComments}</p>
                  </div>
                )}
                {task.feasibilityWorkflowRequired && task.feasibilityWorkflowRequired.length > 0 && (
                  <div>
                    <p className="text-[9px] text-gray-400 font-bold uppercase">Required Additional Workflows</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {task.feasibilityWorkflowRequired.map(wf => (
                        <span key={wf} className="text-[10px] bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded-lg font-bold">
                          {wf}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {task.feasibilityLatitude !== undefined && task.feasibilityLongitude !== undefined && (
                  <div className="pt-2 border-t border-indigo-150/40 flex items-center justify-between">
                    <div>
                      <p className="text-[9px] text-gray-400 font-bold uppercase">GPS Location</p>
                      <p className="text-xs font-mono text-gray-700 font-bold mt-0.5">
                        {task.feasibilityLatitude.toFixed(6)}, {task.feasibilityLongitude.toFixed(6)}
                      </p>
                    </div>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${task.feasibilityLatitude},${task.feasibilityLongitude}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] font-black text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-2.5 py-1 rounded-lg transition-all flex items-center gap-1"
                    >
                      <MapPin size={10} /> Open Maps
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Feedback summary (closed tasks) */}
          {task.status === 'closed' && task.customerRating && (
            <div className="rounded-2xl overflow-hidden"
              style={{ background: 'rgba(168,85,247,0.04)', border: '1px solid rgba(168,85,247,0.12)' }}>
              <div className="flex items-center justify-between px-3.5 py-2.5 border-b"
                style={{ borderColor: 'rgba(168,85,247,0.1)' }}>
                <div className="flex items-center gap-2">
                  <Star size={12} className="text-purple-400" />
                  <p className="text-[9px] font-black text-purple-500 uppercase tracking-widest">Customer Feedback</p>
                </div>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} className={`text-sm leading-none ${i < task.customerRating! ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                  ))}
                  <span className="text-xs font-black text-gray-700 ml-1.5">
                    {['','Poor','Fair','Good','Very Good','Excellent'][task.customerRating]}
                  </span>
                </div>
              </div>
              {task.feedbackCategories && (
                <div className="grid grid-cols-2 divide-x divide-y" style={{ borderColor: 'rgba(168,85,247,0.08)' }}>
                  {([
                    { key: 'serviceQuality', label: 'Service Quality' },
                    { key: 'responseTime',   label: 'Response Time'   },
                    { key: 'communication',  label: 'Communication'   },
                    { key: 'workmanship',    label: 'Workmanship'     },
                  ] as const).map(({ key, label }) => (
                    <div key={key} className="px-3 py-2">
                      <p className="text-[9px] text-gray-400 font-bold">{label}</p>
                      <div className="flex gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <span key={i} className={`text-xs leading-none ${i < (task.feedbackCategories?.[key] ?? 0) ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="px-3.5 py-2.5 flex flex-wrap items-start gap-2 border-t"
                style={{ borderColor: 'rgba(168,85,247,0.1)' }}>
                {task.wouldRecommend !== undefined && (
                  <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${task.wouldRecommend ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                    {task.wouldRecommend ? '👍 Would Recommend' : '👎 Would Not Recommend'}
                  </span>
                )}
                {task.customerFeedback && (
                  <p className="text-xs text-gray-600 italic leading-relaxed w-full">"{task.customerFeedback}"</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Customer feedback form (pending_feedback stage) */}
        {task.status === 'pending_feedback' && (
          <div className="shrink-0 px-5 pb-5 pt-3 border-t border-gray-50">
            {!feedbackOpen ? (
              <button onClick={() => setFeedbackOpen(true)}
                className="w-full py-3.5 text-white font-black rounded-2xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg,#a855f7,#8b5cf6)', boxShadow: '0 4px 16px rgba(168,85,247,0.3)' }}>
                <Star size={16} /> Collect Customer Feedback
              </button>
            ) : (
              <div className="space-y-4">

                {/* Overall rating */}
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Overall Satisfaction</p>
                  <div className="flex gap-1.5">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setRating(n)}
                        className={`text-3xl transition-all hover:scale-110 active:scale-95 leading-none ${n <= rating ? 'text-amber-400 drop-shadow-sm' : 'text-gray-200'}`}>★</button>
                    ))}
                    <span className="ml-2 self-center text-sm font-black text-gray-600">
                      {['','Poor','Fair','Good','Very Good','Excellent'][rating]}
                    </span>
                  </div>
                </div>

                {/* Category ratings */}
                <div className="rounded-2xl border border-gray-100 overflow-hidden divide-y divide-gray-50">
                  {([
                    { key: 'serviceQuality',  label: 'Service Quality'  },
                    { key: 'responseTime',    label: 'Response Time'    },
                    { key: 'communication',   label: 'Communication'    },
                    { key: 'workmanship',     label: 'Workmanship'      },
                  ] as const).map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between px-3.5 py-2.5 bg-white">
                      <span className="text-xs font-bold text-gray-600">{label}</span>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(n => (
                          <button key={n}
                            onClick={() => setCatRatings(prev => ({ ...prev, [key]: n }))}
                            className={`text-base transition-transform hover:scale-110 leading-none ${n <= catRatings[key] ? 'text-amber-400' : 'text-gray-200'}`}>★</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Would recommend */}
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Would Customer Recommend Us?</p>
                  <div className="flex gap-2">
                    {[{ val: true, label: '👍 Yes', activeColor: 'bg-emerald-500' }, { val: false, label: '👎 No', activeColor: 'bg-red-400' }].map(opt => (
                      <button key={String(opt.val)}
                        onClick={() => setWouldRecommend(opt.val)}
                        className={`flex-1 py-2.5 rounded-2xl text-sm font-black border-2 transition-all ${
                          wouldRecommend === opt.val
                            ? `${opt.activeColor} text-white border-transparent`
                            : 'bg-gray-50 text-gray-500 border-gray-100 hover:border-gray-200'
                        }`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Customer Comments <span className="normal-case text-gray-300">(optional)</span></p>
                  <textarea
                    value={feedbackText} onChange={e => setFeedbackText(e.target.value)}
                    placeholder="Any specific feedback, suggestions or complaints from the customer…"
                    rows={3}
                    className="w-full text-sm outline-none text-gray-700 placeholder-gray-400 bg-gray-50 border border-gray-200 rounded-2xl px-3.5 py-3 resize-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all"
                  />
                </div>

                <div className="flex gap-2">
                  <button onClick={handleFeedbackSubmit} disabled={submitting}
                    className="flex-1 py-3.5 text-white font-black rounded-2xl text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                    style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 4px 16px rgba(16,185,129,0.3)' }}>
                    {submitting ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                    Save &amp; Close Ticket
                  </button>
                  <button onClick={() => setFeedbackOpen(false)} disabled={submitting}
                    className="px-4 py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold rounded-2xl text-sm transition-colors">
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Site visit photo upload panel ── */}
        {isSiteVisit && (
          <div className="shrink-0 px-5 pb-2 pt-3 border-t border-gray-50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Camera size={14} className="text-indigo-500" />
                <p className="text-xs font-black text-gray-700">Site Photos</p>
                {savedUrls.length > 0 && (
                  <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    {savedUrls.length} uploaded
                  </span>
                )}
              </div>
              <label className="flex items-center gap-1.5 cursor-pointer px-3 py-1.5 rounded-xl text-[11px] font-black text-white transition-all hover:scale-[1.03] active:scale-[0.97]"
                style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                <ImagePlus size={12} /> Add Photos
                <input type="file" accept="image/*" multiple className="hidden" onChange={handlePickPhotos} />
              </label>
            </div>

            {/* Already saved thumbnails */}
            {savedUrls.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {savedUrls.map((url, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-xl overflow-hidden border border-gray-100 shrink-0">
                    <img src={url} alt={`site-${i}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}

            {/* Pending (not yet uploaded) previews */}
            {pendingPhotos.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ready to upload</p>
                <div className="flex gap-2 flex-wrap">
                  {pendingPhotos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-xl overflow-hidden border-2 border-dashed border-indigo-200 shrink-0 group">
                      <img src={p.preview} alt="preview" className="w-full h-full object-cover opacity-80" />
                      {uploading ? (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <span className="text-white text-[10px] font-black">{Math.round(uploadProgress[i] ?? 0)}%</span>
                        </div>
                      ) : (
                        <button onClick={() => removePending(i)}
                          className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <Trash2 size={14} className="text-white" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button onClick={handleUploadPhotos} disabled={uploading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-white text-xs font-black rounded-xl disabled:opacity-60 transition-all"
                  style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                  {uploading
                    ? <><Loader2 size={13} className="animate-spin" /> Uploading…</>
                    : <><Upload size={13} /> Upload {pendingPhotos.length} Photo{pendingPhotos.length > 1 ? 's' : ''}</>}
                </button>
              </div>
            )}

            {savedUrls.length === 0 && pendingPhotos.length === 0 && (
              <p className="text-[11px] text-amber-600 font-semibold flex items-center gap-1.5">
                <AlertTriangle size={11} /> At least 1 site photo required before proceeding
              </p>
            )}
          </div>
        )}

        {/* Footer — view full task */}
        <div className="shrink-0 px-5 pb-5 pt-3 border-t border-gray-50">
          <button
            onClick={() => { window.location.href = `/tasks/${task.id}`; onClose() }}
            className="w-full py-3.5 text-white font-black rounded-2xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 4px 16px rgba(37,99,235,0.3)' }}>
            <ArrowRight size={16} /> Open Task Details
          </button>
        </div>

        {/* Feasibility form (for inquiries in progress) */}
        {isEnquiryInProgress && (
          <div className="shrink-0 px-5 pb-5 pt-3 border-t border-gray-50 bg-slate-50/50">
            {!feasibilityOpen ? (
              <button
                onClick={() => setFeasibilityOpen(true)}
                className="w-full py-3.5 text-white font-black rounded-2xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)', boxShadow: '0 4px 16px rgba(139,92,246,0.3)' }}
              >
                <Zap size={16} /> Conduct Feasibility Study
              </button>
            ) : (
              <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-1">
                {/* Feasibility Status */}
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Feasibility Status</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFeasStatus('feasible')}
                      className={`flex-1 py-2.5 rounded-xl font-bold text-xs border transition-all ${
                        feasStatus === 'feasible'
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-extrabold shadow-sm'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      ⚡ Feasible
                    </button>
                    <button
                      type="button"
                      onClick={() => setFeasStatus('not_feasible')}
                      className={`flex-1 py-2.5 rounded-xl font-bold text-xs border transition-all ${
                        feasStatus === 'not_feasible'
                          ? 'border-red-500 bg-red-50 text-red-700 font-extrabold shadow-sm'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      ❌ Not Feasible
                    </button>
                  </div>
                </div>

                {/* Additional Workflows Required */}
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Additional Workflow Required</p>
                  <div className="grid grid-cols-2 gap-2">
                    {['Splicing', 'Pulling', 'Support Pole', 'DP Installation'].map(wf => {
                      const isChecked = feasWorkflows.includes(wf)
                      return (
                        <label
                          key={wf}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-[11px] font-bold cursor-pointer transition-all ${
                            isChecked
                              ? 'border-indigo-500 bg-indigo-50/50 text-indigo-700'
                              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              setFeasWorkflows(prev =>
                                isChecked
                                  ? prev.filter(w => w !== wf)
                                  : [...prev, wf]
                              )
                            }}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                          />
                          <span className="truncate">{wf}</span>
                        </label>
                      )
                    })}
                  </div>
                </div>

                {/* GPS Geolocation Section */}
                <div className="bg-white rounded-2xl p-4 border border-gray-200/80 shadow-sm space-y-2.5">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                      <MapPin size={12} className="text-indigo-500" /> GPS Geolocation *
                    </p>
                    <button
                      type="button"
                      onClick={fetchLocation}
                      disabled={fetchingLoc}
                      className="text-[10px] font-black text-indigo-600 hover:text-indigo-850 disabled:opacity-40 flex items-center gap-1 bg-indigo-50 px-2.5 py-1 rounded-lg transition-all"
                    >
                      <RefreshCw size={10} className={fetchingLoc ? 'animate-spin' : ''} />
                      {fetchingLoc ? 'Fetching...' : 'Recalculate'}
                    </button>
                  </div>

                  {fetchingLoc && (
                    <div className="flex items-center gap-2 py-1.5 text-xs text-gray-500">
                      <Loader2 size={13} className="animate-spin text-indigo-500" />
                      <span>Checking current GPS coordinates...</span>
                    </div>
                  )}

                  {!fetchingLoc && locError && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-[11px] text-red-750">
                      <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                      <div>
                        <p className="font-extrabold text-red-800">Location Access Error</p>
                        <p className="text-red-700 font-medium mt-0.5">{locError}</p>
                        <p className="text-[10px] text-red-600/80 mt-1">Please make sure browser location access is allowed for this application and click Recalculate.</p>
                      </div>
                    </div>
                  )}

                  {!fetchingLoc && !locError && feasLat !== undefined && feasLng !== undefined && (
                    <div className="flex items-center justify-between p-3 bg-indigo-50/30 border border-indigo-100 rounded-xl">
                      <div>
                        <p className="text-[11px] font-extrabold text-gray-800 font-mono">Lat: {feasLat.toFixed(6)}</p>
                        <p className="text-[11px] font-extrabold text-gray-800 font-mono mt-0.5">Lng: {feasLng.toFixed(6)}</p>
                      </div>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${feasLat},${feasLng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] font-black text-white bg-indigo-600 hover:bg-indigo-750 px-3 py-1.5 rounded-xl transition-all shadow-sm flex items-center gap-1.5"
                      >
                        <MapPin size={11} /> Open Map
                      </a>
                    </div>
                  )}
                </div>

                {/* Comments */}
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Additional Comments / Remarks</p>
                  <textarea
                    value={feasComments}
                    onChange={e => setFeasComments(e.target.value)}
                    placeholder="Enter fiber distance, port details, obstacles, or reasons if not feasible..."
                    className="w-full text-xs font-semibold text-gray-700 placeholder-gray-400 border border-gray-200 rounded-2xl p-3 outline-none focus:border-indigo-500 transition-colors"
                    rows={3}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleFeasibilitySubmit}
                    disabled={feasSubmitting}
                    className="flex-1 py-3 text-white font-black rounded-2xl text-xs flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                    style={{ background: 'linear-gradient(135deg,#10b981,#059669)', boxShadow: '0 4px 16px rgba(16,185,129,0.3)' }}
                  >
                    {feasSubmitting ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                    Submit Feasibility
                  </button>
                  <button
                    onClick={() => setFeasibilityOpen(false)}
                    disabled={feasSubmitting}
                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold rounded-2xl text-xs transition-colors"
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const EngineerTaskPage = () => {
  const { user } = useAuthStore()
  const engineerId = user?.uid ?? user?.id ?? ''
  const firstName = user?.name?.split(' ')[0] ?? 'there'

  const [myTasks, setMyTasks] = useState<Task[]>([])
  const [openTasks, setOpenTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'mine' | 'open'>('mine')

  const load = useCallback(async () => {
    setLoading(true)
    const myIds = [engineerId, user?.engineerDocId].filter(Boolean) as string[]
    const [mine, open] = await Promise.all([
      taskService.listByAssignee(engineerId, user?.engineerDocId),
      taskService.listUnassigned(),
    ])
    if (mine.success && mine.data) {
      // Strict client-side filter: only show tasks actually assigned to this engineer
      setMyTasks(mine.data.filter(t => t.assignedTo && myIds.includes(t.assignedTo)))
    }
    if (open.success && open.data) {
      // Only truly unassigned tasks — exclude anything with assignedTo already set
      setOpenTasks(open.data.filter(t => !t.assignedTo))
    }
    setLoading(false)
  }, [engineerId, user?.engineerDocId])

  useEffect(() => { load() }, [load])

  // Status changes removed — engineers must open task details to change status

  // For docket/enquiry tasks, pending_approval means engineer is waiting (feasibility submitted or approved but not started)
  const isEngineerWaiting = (t: Task) =>
    t.status === 'pending_approval' && t.type === 'enquiry'

  // Split my tasks: active (needs action), waiting (blocked on admin), done
  const waitingTasks = myTasks.filter(t => isEngineerWaiting(t))
  const activeTasks  = myTasks.filter(t => ACTIVE_STATUSES.includes(t.status) && !isEngineerWaiting(t))
  const doneTasks    = myTasks.filter(t => DONE_STATUSES.includes(t.status))

  const tabs = [
    { key: 'mine' as const, label: 'My Tasks',    count: activeTasks.length + waitingTasks.length, icon: <ClipboardList size={14} /> },
    { key: 'open' as const, label: 'Open Tasks',  count: openTasks.length,   icon: <Inbox size={14} /> },
  ]

  return (
    <div className="space-y-5">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg, #050d1f 0%, #0c1e3d 50%, #0d2a50 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.35) 0%, transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute bottom-0 left-1/4 w-48 h-32 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.2) 0%, transparent 70%)', filter: 'blur(30px)' }} />
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="relative z-10 p-5 lg:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 mb-3">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-[11px] text-blue-200 font-semibold uppercase tracking-wider">Field Engineer</span>
            </div>
            <h1 className="text-xl lg:text-2xl font-black leading-tight">
              Hey, <span style={{ background: 'linear-gradient(135deg,#93c5fd,#67e8f9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{firstName}</span> 👷
            </h1>
            <p className="text-blue-300/70 text-sm mt-1.5">
              {activeTasks.length > 0
                ? `You have ${activeTasks.length} active · ${waitingTasks.length} waiting · ${openTasks.length} open to claim`
                : `${waitingTasks.length > 0 ? `${waitingTasks.length} waiting · ` : ''}${openTasks.length} open tasks available`}
            </p>
          </div>

          {/* Mini stat pills */}
          <div className="flex gap-2 flex-wrap sm:flex-nowrap">
            {[
              { label: 'Active',    value: activeTasks.length,   color: 'text-violet-300' },
              { label: 'Waiting',   value: waitingTasks.length,  color: 'text-amber-300'  },
              { label: 'Done',      value: doneTasks.filter(t => t.status === 'closed').length, color: 'text-emerald-300' },
              { label: 'Available', value: openTasks.length,     color: 'text-cyan-300'   },
            ].map(m => (
              <div key={m.label} className="flex items-center gap-2 px-3.5 py-2 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span className={`text-lg font-black ${m.color}`}>{m.value}</span>
                <span className="text-[10px] text-blue-300/60 font-semibold">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="flex items-center gap-2 bg-white rounded-2xl p-1.5"
        style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-bold transition-all duration-200 ${
              activeTab === tab.key ? 'text-white shadow-md' : 'text-gray-500 hover:text-gray-700'
            }`}
            style={activeTab === tab.key ? { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' } : {}}>
            {tab.icon}
            {tab.label}
            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
              activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
            }`}>{tab.count}</span>
          </button>
        ))}
        <button onClick={load} className="p-2.5 rounded-xl text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all" title="Refresh">
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,rgba(139,92,246,0.1),rgba(99,102,241,0.08))' }}>
            <Loader2 size={22} className="text-violet-500 animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Loading your tasks…</p>
        </div>

      ) : activeTab === 'mine' ? (
        <div className="space-y-5">
          {/* Active tasks */}
          {activeTasks.length === 0 && waitingTasks.length === 0 && doneTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg,rgba(139,92,246,0.07),rgba(99,102,241,0.05))', border: '1px solid rgba(99,102,241,0.1)' }}>
                <ClipboardList size={26} className="text-gray-300" />
              </div>
              <div>
                <p className="font-black text-gray-700 text-base">No tasks assigned yet</p>
                <p className="text-sm text-gray-400 mt-1">Check "Open Tasks" to claim available work</p>
              </div>
              <button onClick={() => setActiveTab('open')}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-xl"
                style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
                <Inbox size={14} /> View Open Tasks
              </button>
            </div>
          ) : (
            <>
              {activeTasks.length > 0 && (
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">
                    Active · {activeTasks.length}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {activeTasks.map(task => (
                      <TaskCard key={task.id} task={task}
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        onAction={t => navigate(`/tasks/${t.id}`)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {waitingTasks.length > 0 && (
                <div>
                  <p className="text-[10px] font-black text-amber-500 uppercase tracking-widest mb-3 px-1">
                    Waiting · {waitingTasks.length}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 opacity-70">
                    {waitingTasks.map(task => (
                      <TaskCard key={task.id} task={task}
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        onAction={t => navigate(`/tasks/${t.id}`)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {doneTasks.length > 0 && (
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">
                    Done · {doneTasks.length}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 opacity-60">
                    {doneTasks.map(task => (
                      <TaskCard key={task.id} task={task}
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        onAction={() => navigate(`/tasks/${task.id}`)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      ) : (
        // Open tasks tab
        openTasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,rgba(16,185,129,0.07),rgba(5,150,105,0.05))', border: '1px solid rgba(16,185,129,0.1)' }}>
              <AlertTriangle size={26} className="text-gray-300" />
            </div>
            <div>
              <p className="font-black text-gray-700 text-base">No open tasks right now</p>
              <p className="text-sm text-gray-400 mt-1">All tasks have been assigned. Check back soon.</p>
            </div>
          </div>
        ) : (
          <div>
            {/* Claim banner */}
            <div className="flex items-center gap-3 mb-4 px-4 py-3 rounded-2xl"
              style={{ background: 'linear-gradient(135deg,rgba(6,182,212,0.08),rgba(37,99,235,0.05))', border: '1px solid rgba(6,182,212,0.15)' }}>
              <Inbox size={16} className="text-cyan-500 shrink-0" />
              <p className="text-sm text-gray-600">
                <span className="font-black text-cyan-600">{openTasks.length} unassigned task{openTasks.length > 1 ? 's' : ''}</span>
                {' '}— open a task to view details and claim.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {openTasks.map(task => (
                <TaskCard key={task.id} task={task}
                  onClick={() => navigate(`/tasks/${task.id}`)}
                  onAction={() => navigate(`/tasks/${task.id}`)}
                />
              ))}
            </div>
          </div>
        )
      )}

    </div>
  )
}

export default EngineerTaskPage
